import type { ComponentRenderProps } from "@json-render/react";
import { z } from "zod";

import {
  parseReportTextStyle,
  REPORT_TEXT_STYLE_DESCRIPTION,
  reportTextStyleSchema,
} from "@/components/ui/report/reportPresentation";

import {
  REPORT_TABLE_OF_CONTENTS_SEPARATORS,
  REPORT_TABLE_OF_CONTENTS_STYLES,
  ReportTableOfContents,
  type ReportTableOfContentsEntry,
} from "./ReportTableOfContents";

const reportTableOfContentsPropsSchema = z.object({
  style: z.enum(REPORT_TABLE_OF_CONTENTS_STYLES).nullable().optional(),
  separator: z.enum(REPORT_TABLE_OF_CONTENTS_SEPARATORS).nullable().optional(),
  numbered: z.boolean().nullable().optional(),
  linkStyle: reportTextStyleSchema.nullable().optional(),
  separatorStyle: reportTextStyleSchema.nullable().optional(),
});

/** Layout Studio catalog definition for ReportTableOfContents. Entries are derived at render, never authored. */
export const ReportTableOfContentsSpec = {
  props: reportTableOfContentsPropsSchema,
  description: `Table of contents. Add it only when the user asks for one. Its entries are built from the titled ReportSection blocks at render time, so never emit sections or links. style is list (default: vertical numbered list under a "Contents" label) or inline (one wrapping line of section title links with no label, e.g. Top Stories | Policy | Competitive Watch); use inline whenever the user asks for a horizontal, inline or one-line table of contents. separator is pipe (default, |), bullet (•) or dot (·), used only for inline. numbered defaults to true for list and false for inline; set it only to override. Section titles follow the theme font, and inline links use the theme primary colour. Optional presentation (omit for the default look): linkStyle sets section title typography and separatorStyle sets the inline separators (${REPORT_TEXT_STYLE_DESCRIPTION}; textAlign has no effect). Set them only when the user asks, e.g. a link font colour, and keep every chosen prop on unrelated edits. It renders after the banner and the header, or the date when there is no header.`,
  example: { style: "list" },
} as const;

/** Adapter for json-render defineRegistry. Herald authors it with no props; prepareReportSpecForRender fills in sections. */
export const ReportTableOfContentsFromProps = ({
  element,
}: ComponentRenderProps) => {
  const { sections = [], ...authored } = element.props as {
    sections?: ReportTableOfContentsEntry[];
  } & Record<string, unknown>;
  const { shape } = reportTableOfContentsPropsSchema;

  return (
    <ReportTableOfContents
      sections={sections}
      style={shape.style.safeParse(authored.style).data ?? undefined}
      separator={
        shape.separator.safeParse(authored.separator).data ?? undefined
      }
      numbered={shape.numbered.safeParse(authored.numbered).data ?? undefined}
      linkStyle={parseReportTextStyle(authored.linkStyle)}
      separatorStyle={parseReportTextStyle(authored.separatorStyle)}
    />
  );
};
